import { groupFoundationsByAlignment } from "./alignmentGrouping";
import { computeGridLineCoverage } from "./gridLineCoverage";
import { selectGridLines } from "./gridLineSelection";
import { getGroupAlignmentAxes, localToWorld } from "./gridSearchSpace";
/**
 * Assigns each foundation the local x/y positions of the selected grid lines
 * surrounding it. A selected x line's "near" set covers foundations on their
 * left (lower x); its "far" set covers them on their right (higher x). A
 * selected y line's "near" set covers foundations below (lower y = "down");
 * its "far" set covers them above ("up"). A side with no covering line is
 * left undefined — no fallback position, since that line simply isn't drawn.
 */
export function assignFoundationGridSides(group, selectedLines) {
    const left = new Map();
    const right = new Map();
    const down = new Map();
    const up = new Map();
    for (const line of selectedLines.x) {
        for (const id of line.near)
            left.set(id, line.position);
        for (const id of line.far)
            right.set(id, line.position);
    }
    for (const line of selectedLines.y) {
        for (const id of line.near)
            down.set(id, line.position);
        for (const id of line.far)
            up.set(id, line.position);
    }
    const sides = new Map();
    for (const foundation of group.foundations) {
        sides.set(foundation.id, {
            left: left.get(foundation.id),
            right: right.get(foundation.id),
            down: down.get(foundation.id),
            up: up.get(foundation.id),
        });
    }
    return sides;
}
const CORNERS = {
    bl: { xSide: "left", ySide: "down" },
    br: { xSide: "right", ySide: "down" },
    tr: { xSide: "right", ySide: "up" },
    tl: { xSide: "left", ySide: "up" },
};
const CORNER_ORDER = ["bl", "br", "tr", "tl"];
/** Builds the routing-graph nodes/edges for one foundation's grid rectangle. Sides that are
 * missing (undefined) simply omit the corners/edges that would depend on them. */
function buildFoundationRectangleGraph(foundationId, gridSides, axes) {
    const nodeIdByCorner = new Map();
    const nodes = [];
    for (const corner of CORNER_ORDER) {
        const { xSide, ySide } = CORNERS[corner];
        const x = gridSides[xSide];
        const y = gridSides[ySide];
        if (x === undefined || y === undefined)
            continue;
        const id = `earthing-grid-${foundationId}-${corner}`;
        const [worldX, worldY] = localToWorld([x, y], axes);
        nodeIdByCorner.set(corner, id);
        nodes.push({ id, posM: [worldX, worldY, 0], kind: "JUNCTION" });
    }
    const edges = [];
    for (let i = 0; i < CORNER_ORDER.length; i++) {
        const fromCorner = CORNER_ORDER[i];
        const toCorner = CORNER_ORDER[(i + 1) % CORNER_ORDER.length];
        const from = nodeIdByCorner.get(fromCorner);
        const to = nodeIdByCorner.get(toCorner);
        if (from === undefined || to === undefined)
            continue;
        edges.push({
            id: `earthing-grid-${foundationId}-edge-${i}`,
            from,
            to,
            compCode: "EARTHING-GRID",
            profile: "CIRCULAR",
            size: { diameterM: 0.02 },
        });
    }
    return { nodes, edges };
}
/** Builds the grid rectangle graph for every foundation in one alignment group. */
export function buildGroupEarthingGrid(group) {
    const axes = getGroupAlignmentAxes(group);
    const coverage = computeGridLineCoverage(group);
    const selectedLines = selectGridLines(coverage);
    const gridSidesByFoundation = assignFoundationGridSides(group, selectedLines);
    const nodes = [];
    const edges = [];
    for (const foundation of group.foundations) {
        const gridSides = gridSidesByFoundation.get(foundation.id);
        const fragment = buildFoundationRectangleGraph(foundation.id, gridSides, axes);
        nodes.push(...fragment.nodes);
        edges.push(...fragment.edges);
    }
    return { nodes, edges };
}
/**
 * Builds the full earthing grid graph for a set of apparatus foundations:
 * groups them by alignment, then builds and merges each group's grid.
 */
export function buildEarthingGrid(apparatusFoundations) {
    const groups = groupFoundationsByAlignment(apparatusFoundations);
    const nodes = [];
    const edges = [];
    for (const group of groups) {
        const fragment = buildGroupEarthingGrid(group);
        nodes.push(...fragment.nodes);
        edges.push(...fragment.edges);
    }
    return { nodes, edges };
}
